import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import Icon from '@/components/Icon';
import { useAuth } from '@/auth/AuthContext';
import { useWsEvent } from '@/auth/WsProvider';
import { ApiError, api } from '@/lib/api';
import { PCChildBar, PCYearBar, PCArchiveBanner } from './_common';
import './styles.css';

/**
 * Layout личного кабинета родителя.
 *
 * Здесь же грузим `/api/parent/me` — единственный источник правды про
 * детей и их историю учебных лет. Через `ParentShellContext` отдаём
 * страницам выбранного ребёнка и год + сеттеры.
 *
 * Логика выбора по умолчанию:
 *   • активный ребёнок — первый из списка (`me.children[0]`);
 *   • активный год — текущий (`y.current === true`), иначе — самый
 *     поздний из истории этого ребёнка.
 *
 * При смене ребёнка год пересчитывается: у нового ребёнка истории годов
 * другие, чужой `activeYearId` может не существовать.
 */

const ParentShellContext = createContext(null);
export function useParentShell() {
  const ctx = useContext(ParentShellContext);
  if (!ctx) throw new Error('useParentShell вне ParentShell');
  return ctx;
}

const NAV_EDU = [
  { id: 'home',            to: '/parent',                 label: 'Главная',              icon: 'home',     end: true },
  { id: 'diary',           to: '/parent/journal',         label: 'Дневник',              icon: 'book' },
  { id: 'schedule',        to: '/parent/schedule',        label: 'Расписание',           icon: 'calendar' },
  { id: 'payments',        to: '/parent/payments',        label: 'Оплата',               icon: 'wallet' },
  { id: 'extracurricular', to: '/parent/extracurricular', label: 'Внеурочные занятия',   icon: 'sparkle' },
  { id: 'extras',          to: '/parent/extras',          label: 'Дополнительные услуги',icon: 'tag' },
];
const NAV_TALK = [
  { id: 'messages',      to: '/parent/messages',      label: 'Сообщения',            icon: 'chat' },
  // Скрыто до реализации: «Задать вопрос», «Записаться на встречу»,
  // «Уведомления». Маршруты с соответствующими placeholder'ами в
  // App.jsx тоже временно убраны.
];

export default function ParentShell() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const [me, setMe]         = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const [activeChildId, setActiveChildId] = useState(null);
  const [activeYearId,  setActiveYearId]  = useState(null);

  // Загрузка карточки родителя.
  useEffect(() => {
    let cancelled = false;
    setLoad(true); setError(null);
    api.get('/api/parent/me')
      .then((res) => {
        if (cancelled) return;
        setMe(res);
        const first = res.children?.[0];
        if (first) {
          setActiveChildId(first.id);
          const cur = (first.years || []).find((y) => y.current);
          const pick = cur || (first.years || [])[(first.years?.length || 1) - 1];
          if (pick) setActiveYearId(pick.id);
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, []);

  // Активный ребёнок и его годы.
  const child = me?.children?.find((c) => c.id === activeChildId) || me?.children?.[0] || null;
  const years = child?.years || [];
  const activeYear = years.find((y) => y.id === activeYearId) || years.find((y) => y.current) || years[years.length - 1] || null;

  // При смене ребёнка — если выбранный год не существует в его истории,
  // переключаемся на его текущий (или последний).
  useEffect(() => {
    if (!child) return;
    if (!years.some((y) => y.id === activeYearId)) {
      const cur = years.find((y) => y.current);
      const pick = cur || years[years.length - 1];
      setActiveYearId(pick?.id || null);
    }
  }, [child?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Детсадовский год: расписание и журнал (в школьном понимании) недоступны.
  // Фильтруем пункты навигации и заодно — если родитель был на этих
  // страницах для школьника, потом переключился на сад — редиректим
  // на «Главную».
  const isKg = !!activeYear?.isKg;
  const navEdu = isKg
    ? NAV_EDU.filter((n) => n.id !== 'schedule' && n.id !== 'diary')
    : NAV_EDU;
  const navAll = [...navEdu, ...NAV_TALK];
  // /parent/grades — подстраница «Дневника» (открывается по ссылке
  // «Подробнее по предмету →»). Подкрашиваем тот же пункт в меню.
  const normalizedPath = location.pathname.startsWith('/parent/grades')
    ? '/parent/journal'
    : location.pathname;
  const activeNav = navAll.find((n) => (n.end ? normalizedPath === n.to : normalizedPath.startsWith(n.to))) || navAll[0];
  const kgInaccessible = isKg && (
    location.pathname.startsWith('/parent/schedule') ||
    location.pathname.startsWith('/parent/journal') ||
    location.pathname.startsWith('/parent/grades')
  );

  // Сводка для бейджа долга — пока вычислим лениво: бейдж появится после
  // первой загрузки /api/parent/dashboard в Home. Для shell-уровня
  // храним признак отдельно (Home проставит через context).
  const [hasDebt, setHasDebt] = useState(false);

  // Непрочитанные сообщения от школы — красный бейдж на пункте «Сообщения».
  const [unreadChat, setUnreadChat] = useState(0);
  const reloadUnread = () => {
    api.get('/api/chats/unread')
      .then((r) => setUnreadChat(Number(r?.unread || 0)))
      .catch(() => {});
  };
  useEffect(reloadUnread, []);
  useWsEvent('chat.message', reloadUnread);

  const ctxValue = useMemo(() => ({
    me,
    parent: me?.parent || null,
    children: me?.children || [],
    child,
    setActiveChildId,
    years,
    activeYear,
    setActiveYearId,
    setHasDebt, // Home сообщит, чтобы загорелся бейдж «1» на «Оплата»
  }), [me, child, years, activeYear]);

  return (
    <ParentShellContext.Provider value={ctxValue}>
      <div className="pc pc-app">
        <PCSidebar
          activeId={activeNav.id}
          hasDebt={hasDebt}
          unreadChat={unreadChat}
          parent={me?.parent}
          onLogout={logout}
          navEdu={navEdu}
        />
        <main className="pc-app__main">
          <div className="pc-head">
            <div>
              <div className="pc-head__title">{activeNav.label}</div>
              <div className="pc-head__sub">
                {activeYear?.label}{activeYear && (activeYear?.classLabel || child?.class) ? ' · ' : ''}{activeYear?.classLabel || child?.class || ''}
              </div>
            </div>
            <div className="pc-head__spacer" />
            {/* Мобильная кнопка профиля — на десктопе профиль уже есть
                в левом сайдбаре. На мобиле сайдбар скрыт, поэтому
                выносим круглый аватар в правый угол шапки. */}
            <MobileHeaderUser
              parent={me?.parent}
              onLogout={logout}
            />
          </div>

          {loading && (
            <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем кабинет…</div>
          )}
          {error && (
            <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>
          )}

          {/* К учётке не привязан ни один ученик (доступ только создали,
              либо привязку сняли) — говорим об этом прямо, а не показываем
              пустую страницу. */}
          {!loading && !error && !child && (
            <div className="pc-body">
              <div style={{
                maxWidth: 520, margin: '48px auto', padding: '28px 24px',
                background: 'var(--pc-panel, #fff)',
                border: '1px solid var(--pc-border, #e5e7eb)',
                borderRadius: 12, textAlign: 'center',
              }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>👋</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  К вашей учётной записи пока не привязан ни один ученик
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--pc-fg-faint, #6b7280)', lineHeight: 1.5 }}>
                  Так бывает, если доступ в кабинет только что создали или привязку
                  к ребёнку сняли. Обратитесь к администратору школы — после привязки
                  здесь появятся данные вашего ребёнка: успеваемость, расписание и оплата.
                </div>
                <button
                  type="button"
                  className="btn btn--sm"
                  style={{ marginTop: 18 }}
                  onClick={logout}
                >
                  Выйти из кабинета
                </button>
              </div>
            </div>
          )}

          {!loading && !error && child && (
            <>
              {(me.children.length > 1) && (
                <PCChildBar
                  children={me.children}
                  activeId={child.id}
                  onChoose={setActiveChildId}
                />
              )}
              {years.length > 0 && (
                <PCYearBar
                  years={years}
                  activeYear={activeYear?.id}
                  onChoose={setActiveYearId}
                />
              )}
              {activeYear && !activeYear.current && !activeYear.upcoming && (
                <PCArchiveBanner year={activeYear} />
              )}
              {kgInaccessible
                ? <Navigate to="/parent" replace />
                : <Outlet />}
            </>
          )}
        </main>

        <PCTabBar activeId={activeNav.id} hasDebt={hasDebt} unreadChat={unreadChat} isKg={isKg} />
      </div>
    </ParentShellContext.Provider>
  );
}

// ── Боковое меню (десктоп) ──────────────────────────────────────────
function PCSidebar({ activeId, hasDebt, unreadChat = 0, parent, onLogout, navEdu = NAV_EDU }) {
  const initials = parent
    ? ((parent.fullName || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?')
    : '?';
  const display = parent
    ? (() => {
        const parts = (parent.fullName || '').trim().split(/\s+/);
        return parts.length >= 2 ? (parts[1] + ' ' + parts[0]) : (parent.fullName || '');
      })()
    : '';
  return (
    <aside className="pc-side">
      <Link to="/parent" className="pc-side__brand" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="pc-side__logo">НП</div>
        <div>
          <div className="pc-side__brand-name">Новое Поколение</div>
          <div className="pc-side__brand-sub">Кабинет родителя</div>
        </div>
      </Link>

      <div className="pc-side__nav">
        <div className="pc-side__title">Образование</div>
        {navEdu.map((it) => (
          <NavItem key={it.id} item={it} activeId={activeId}
                   badge={it.id === 'payments' && hasDebt ? '1' : null} />
        ))}
        <div className="pc-side__title" style={{ marginTop: 16 }}>Связь</div>
        {NAV_TALK.map((it) => (
          <NavItem
            key={it.id}
            item={it}
            activeId={activeId}
            badge={it.id === 'messages' && unreadChat > 0
              ? (unreadChat > 99 ? '99+' : String(unreadChat))
              : null}
          />
        ))}
      </div>

      <ParentProfileMenu
        initials={initials}
        display={display || 'Родитель'}
        email={parent?.email || ''}
        onLogout={onLogout}
      />
    </aside>
  );
}

/**
 * Профиль в подвале сайдбара — кнопка с попапом. По клику открывается
 * меню «Уведомления / Профиль / Выход». Закрывается по клику снаружи,
 * по Esc и при переходе по любому пункту.
 */
function ParentProfileMenu({ initials, display, email, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="pc-side__profile-wrap" ref={wrapRef}>
      {open && (
        <div className="pc-side__profile-menu" role="menu">
          <Link
            to="/parent/profile"
            className="pc-side__profile-menu-item"
            onClick={() => setOpen(false)}
            role="menuitem"
          >
            <Icon name="badge" size={14} /> Профиль
          </Link>
          <div className="pc-side__profile-menu-sep" />
          <button
            type="button"
            className="pc-side__profile-menu-item pc-side__profile-menu-item--danger"
            onClick={() => { setOpen(false); onLogout(); }}
            role="menuitem"
          >
            <Icon name="external" size={14} /> Выход
          </button>
        </div>
      )}
      <button
        type="button"
        className={'pc-side__profile' + (open ? ' is-open' : '')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="pc-side__avatar">{initials}</div>
        <div className="pc-side__profile-text">
          <div className="pc-side__profile-name">{display}</div>
          <div className="pc-side__profile-mail">{email}</div>
        </div>
        <Icon name="chevronDown" size={12} className="pc-side__profile-chevron" />
      </button>
    </div>
  );
}

/**
 * Аватар-кнопка в правом верхнем углу шапки (видна только на мобиле,
 * где сайдбар скрыт). По тапу — попап с Уведомления / Профиль / Выход.
 */
function MobileHeaderUser({ parent, onLogout }) {
  const initials = parent
    ? ((parent.fullName || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?')
    : '?';
  const display = parent
    ? (() => {
        const parts = (parent.fullName || '').trim().split(/\s+/);
        return parts.length >= 2 ? (parts[1] + ' ' + parts[0]) : (parent.fullName || '');
      })()
    : '';

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="pc-head__user" ref={wrapRef}>
      <button
        type="button"
        className={'pc-head__user-btn' + (open ? ' is-open' : '')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={display || 'Профиль'}
      >
        {initials}
      </button>
      {open && (
        <div className="pc-head__user-menu" role="menu">
          {parent && (
            <div className="pc-head__user-info">
              <div className="pc-head__user-info-name">{display}</div>
              {parent.email && (
                <div className="pc-head__user-info-mail">{parent.email}</div>
              )}
            </div>
          )}
          <Link
            to="/parent/profile"
            className="pc-head__user-item"
            onClick={() => setOpen(false)}
            role="menuitem"
          >
            <Icon name="badge" size={14} /> Профиль
          </Link>
          <div className="pc-head__user-sep" />
          <button
            type="button"
            className="pc-head__user-item pc-head__user-item--danger"
            onClick={() => { setOpen(false); onLogout && onLogout(); }}
            role="menuitem"
          >
            <Icon name="external" size={14} /> Выход
          </button>
        </div>
      )}
    </div>
  );
}

function NavItem({ item, activeId, badge }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        'pc-nav-item' + (isActive || activeId === item.id ? ' is-active' : '')
      }
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Icon name={item.icon} size={16} className="ico" />
      {item.label}
      {badge && <span className="pc-nav-item__badge">{badge}</span>}
    </NavLink>
  );
}

// ── Нижний таб-бар (мобайл) ─────────────────────────────────────────
function PCTabBar({ activeId, hasDebt, unreadChat = 0, isKg = false }) {
  // У детсадовцев нет «Расписания» и «Дневника» — пятая ячейка тоже
  // «Ещё», просто состав попапа другой. Сами вкладки 1..4 — самые
  // частые экраны. Остальные пункты меню доступны через «Ещё».
  const tabs = isKg
    ? [
        { id: 'home',     to: '/parent',          label: 'Главная',     icon: 'home',     end: true },
        { id: 'payments', to: '/parent/payments', label: 'Оплата',      icon: 'wallet' },
        { id: 'messages', to: '/parent/messages', label: 'Сообщения',   icon: 'chat' },
      ]
    : [
        { id: 'home',     to: '/parent',          label: 'Главная',     icon: 'home',     end: true },
        { id: 'diary',    to: '/parent/journal',  label: 'Дневник',     icon: 'book' },
        { id: 'schedule', to: '/parent/schedule', label: 'Расписание',  icon: 'calendar' },
        { id: 'payments', to: '/parent/payments', label: 'Оплата',      icon: 'wallet' },
      ];

  // Что показать в попапе «Ещё»: всё из навигации, что не попало на
  // главную линейку. Берём из NAV_EDU + NAV_TALK, фильтруем по id.
  const tabIds = new Set(tabs.map((t) => t.id));
  const eduForMode = isKg ? NAV_EDU.filter((n) => n.id !== 'schedule' && n.id !== 'diary') : NAV_EDU;
  const moreItems = [...eduForMode, ...NAV_TALK].filter((n) => !tabIds.has(n.id));

  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  // Закрываем «Ещё» при смене страницы.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);
  // Закрываем по Esc.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const renderBadge = (id) => {
    if (id === 'payments' && hasDebt) {
      return <span className="pc-m__tab-badge">1</span>;
    }
    if (id === 'messages' && unreadChat > 0) {
      return (
        <span className="pc-m__tab-badge">{unreadChat > 99 ? '99+' : unreadChat}</span>
      );
    }
    return null;
  };

  // Бейдж на «Ещё» — если какой-то пункт из попапа имеет непрочитанные.
  const moreHasUnread = moreItems.some(
    (n) => (n.id === 'messages' && unreadChat > 0)
  );

  return (
    <>
      {moreOpen && (
        <div className="pc-m__more-backdrop" onClick={() => setMoreOpen(false)} />
      )}
      <nav
        className="pc-m__tabbar"
        style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, 1fr)` }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.id}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              'pc-m__tab' + (isActive || activeId === t.id ? ' is-active' : '')
            }
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Icon name={t.icon} size={20} />
            {t.label}
            {renderBadge(t.id)}
          </NavLink>
        ))}

        {/* Пятая кнопка «Ещё» — открывает попап с остальными пунктами. */}
        <button
          type="button"
          className={'pc-m__tab pc-m__tab-more' + (moreOpen ? ' is-active' : '')}
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-haspopup="menu"
        >
          <Icon name="more" size={20} />
          Ещё
          {moreHasUnread && !moreOpen && (
            <span className="pc-m__tab-badge">•</span>
          )}
        </button>

        {moreOpen && (
          <div className="pc-m__more" role="menu">
            {moreItems.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.end}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) =>
                  'pc-m__more-item' + (isActive ? ' is-active' : '')
                }
                style={{ textDecoration: 'none', color: 'inherit' }}
                role="menuitem"
              >
                <Icon name={n.icon} size={18} />
                <span>{n.label}</span>
                {n.id === 'messages' && unreadChat > 0 && (
                  <span className="pc-m__tab-badge" style={{ marginLeft: 'auto' }}>
                    {unreadChat > 99 ? '99+' : unreadChat}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}
